// Vanuatu National Syllabus: French as a Foreign Language (FLE) - Junior Secondary (Years 7-10)
// Based on official Vanuatu Ministry of Education Teacher Guides
// Last Updated: February 5, 2026

export const frenchCurriculum = {
  subject: "French as a Foreign Language (Français Langue Étrangère)",
  level: "Junior Secondary (Years 7-10)",
  yearLevels: ["year7", "year8", "year9", "year10"],
  
  year7: {
    title: "Year 7 French - FLE",
    description: "Introduction to French language through self-presentation, daily life, and Francophone culture. Students develop basic communication skills in listening, speaking, reading, and writing.",
    strands: [
      {
        id: "y7-fr-s1",
        name: "Term 1: Se présenter (Introducing Yourself)",
        code: "FRE-T1",
        color: "blue",
        units: [
          {
            id: "y7-t1-u1",
            name: "Unité 1: Se présenter - Prénom et âge",
            weeks: "1-3",
            duration: "6 hours",
            topics: [
              "Talking about oneself (name, age)",
              "Spelling names in French",
              "Counting numbers 1-20",
              "Creating an identity card",
              "Comparing greetings in France and Vanuatu"
            ],
            objectives: [
              "Introduce oneself using je m'appelle and j'ai ... ans",
              "Spell names using the French alphabet",
              "Count and use numbers in context",
              "Understand and complete simple identity documents"
            ]
          },
          {
            id: "y7-t1-u2",
            name: "Unité 2: Présenter sa famille et ses langues",
            weeks: "4-6",
            duration: "6 hours",
            topics: [
              "Family vocabulary (père, mère, frère, sœur)",
              "Naming colors and basic vocabulary",
              "Talking about languages spoken",
              "Understanding multilingualism in Vanuatu"
            ],
            objectives: [
              "Present family members",
              "Describe using basic adjectives and colors",
              "Talk about languages: Je parle français, anglais, bislama"
            ]
          },
          {
            id: "y7-t1-u3",
            name: "Unité 3: Situations concrètes",
            weeks: "7-9",
            duration: "6 hours",
            topics: [
              "Self-introduction in real situations",
              "French alphabet and phonetics",
              "Simple sentence structure",
              "Gender of nouns",
              "Negative and interrogative sentences"
            ],
            objectives: [
              "Introduce oneself in various contexts",
              "Form simple French sentences",
              "Use dialogue and games for communication"
            ]
          },
          {
            id: "y7-t1-u4",
            name: "Unité 4: Sur le chemin du collège",
            weeks: "10-12",
            duration: "6 hours",
            topics: [
              "School vocabulary and supplies",
              "Shops and transport vocabulary",
              "Prepositions of place (à la/au, à/en)",
              "Road signs and directions",
              "Describing journeys to school"
            ],
            objectives: [
              "Navigate using directions",
              "Use prepositions correctly",
              "Describe daily routines and journeys"
            ]
          }
        ]
      },
      {
        id: "y7-fr-s2",
        name: "Term 2: Le français et la francophonie",
        code: "FRE-T2",
        color: "green",
        units: [
          {
            id: "y7-t2-u1",
            name: "Unité 1: La francophonie",
            weeks: "1-4",
            duration: "8 hours",
            topics: [
              "French in family and community",
              "Francophone countries in the world",
              "Understanding French in context",
              "Vocabulary development through real situations"
            ],
            objectives: [
              "Identify Francophone territories",
              "Develop curiosity for French language",
              "Use vocabulary in communication situations"
            ]
          },
          {
            id: "y7-t2-u2",
            name: "Unité 2: Se préparer pour le collège",
            weeks: "5-8",
            duration: "8 hours",
            topics: [
              "School supplies vocabulary",
              "Classroom furniture",
              "Transport and shops vocabulary",
              "Spatial orientation and object descriptions",
              "Verb conjugation basics"
            ],
            objectives: [
              "Memorize school-related vocabulary",
              "Describe objects and locations",
              "Ask and answer questions about needs"
            ]
          },
          {
            id: "y7-t2-u3",
            name: "Unité 3: Les sports et la musique",
            weeks: "9-12",
            duration: "8 hours",
            topics: [
              "Sports vocabulary",
              "Musical instruments",
              "Expressing tastes and preferences",
              "Physical descriptions",
              "Rhymes and songs in French"
            ],
            objectives: [
              "Talk about sports and music",
              "Express likes and dislikes",
              "Enrich vocabulary through songs"
            ]
          }
        ]
      },
      {
        id: "y7-fr-s3",
        name: "Term 3: La vie au collège",
        code: "FRE-T3",
        color: "purple",
        units: [
          {
            id: "y7-t3-u1",
            name: "Unité 1: Une journée au collège",
            weeks: "1-4",
            duration: "8 hours",
            topics: [
              "Telling time (l'heure)",
              "Days of the week",
              "School timetable and subjects",
              "Comparing schedules",
              "Talking about sports and athletes"
            ],
            objectives: [
              "Tell time in French",
              "Discuss school schedules",
              "Express preferences and justify opinions"
            ]
          },
          {
            id: "y7-t3-u2",
            name: "Unité 2: Arts visuels",
            weeks: "5-8",
            duration: "8 hours",
            topics: [
              "Visual art vocabulary",
              "Analyzing images and posters",
              "Vanuatu and international artists",
              "Describing artworks",
              "Cultural appreciation"
            ],
            objectives: [
              "Describe visual artworks",
              "Analyze images and their messages",
              "Express tastes and emotions about art"
            ]
          },
          {
            id: "y7-t3-u3",
            name: "Unité 3: La musique",
            weeks: "9-10",
            duration: "4 hours",
            topics: [
              "Music vocabulary",
              "French songs and artists",
              "Identifying rhymes",
              "Creating short reports on artists",
              "Expressing sensations and emotions"
            ],
            objectives: [
              "Listen to and discuss French music",
              "Identify poetic elements in songs",
              "Present information about artists"
            ]
          }
        ]
      },
      {
        id: "y7-fr-s4",
        name: "Term 4: Projets et révision",
        code: "FRE-T4",
        color: "orange",
        units: [
          {
            id: "y7-t4-u1",
            name: "Unité 1: Projet culturel",
            weeks: "1-4",
            duration: "8 hours",
            topics: [
              "Cultural project planning",
              "Research skills in French",
              "Presentation preparation",
              "Creative expression",
              "Francophone culture exploration"
            ],
            objectives: [
              "Plan and execute a cultural project",
              "Research using French sources",
              "Present findings in French"
            ]
          },
          {
            id: "y7-t4-u2",
            name: "Unité 2: Révision annuelle",
            weeks: "5-10",
            duration: "12 hours",
            topics: [
              "Vocabulary consolidation",
              "Grammar review",
              "Oral skills practice",
              "Written expression review",
              "Exam preparation"
            ],
            objectives: [
              "Master all Year 7 vocabulary",
              "Demonstrate grammar competence",
              "Prepare for Year 8 French"
            ]
          }
        ]
      }
    ],
    assessmentWeighting: {
      oralComprehension: 25,
      oralExpression: 25,
      readingComprehension: 25,
      writtenExpression: 25
    },
    assessmentTypes: {
      formative: ["Self-assessment sheets", "Observation grids", "Peer correction", "Oral participation"],
      summative: ["Oral presentations", "Written tests", "Listening comprehension", "Identity card creation"]
    }
  },
  
  year8: {
    title: "Year 8 French - FLE",
    description: "Building on Year 7 foundations, students explore community life, traditions, and environmental themes while developing more complex language structures.",
    strands: [
      {
        id: "y8-fr-s1",
        name: "Term 1: Le français et nous",
        code: "FRE-T1",
        color: "blue",
        units: [
          {
            id: "y8-t1-u1",
            name: "Unité 1: Le français et nous",
            weeks: "1-6",
            duration: "12 hours",
            topics: [
              "Classroom communication situations",
              "Comparison of school life in Vanuatu and France",
              "The Francophonie in Vanuatu",
              "Personal language learning methodology"
            ],
            objectives: [
              "Develop comprehension through varied situations",
              "Activate and build on Year 7 knowledge",
              "Compare cultural aspects of school life"
            ],
            assessment: "Summative: Oral comprehension and written expression (Week 6)"
          },
          {
            id: "y8-t1-u2",
            name: "Unité 2: Faisons connaissance",
            weeks: "7-11",
            duration: "10 hours",
            topics: [
              "Physical descriptions of people",
              "Asking and answering personal questions",
              "Games for learning vocabulary",
              "Commerce and profession vocabulary",
              "Affirmative, negative, interrogative sentences"
            ],
            objectives: [
              "Describe people physically",
              "Obtain precise information through interaction",
              "Connect French with other languages",
              "Structure complex sentences"
            ],
            assessment: "Summative: Comprehension and written expression (Week 11)"
          }
        ]
      },
      {
        id: "y8-fr-s2",
        name: "Term 2: La vie dans la communauté",
        code: "FRE-T2",
        color: "green",
        units: [
          {
            id: "y8-t2-u1",
            name: "Unité 1: Du jardin au repas",
            weeks: "1-6",
            duration: "12 hours",
            topics: [
              "Garden to market vocabulary",
              "Garden stories and narratives",
              "Planting and harvesting calendars",
              "Food and cuisine vocabulary",
              "Organizing activities (picnic planning)",
              "Plurilingual approaches to understanding"
            ],
            objectives: [
              "Develop reading and written expression",
              "Understand repetitive structure stories",
              "Write for various purposes",
              "Enrich vocabulary related to food and actions"
            ],
            assessment: "Summative: Oral and written expression (Week 6)"
          },
          {
            id: "y8-t2-u2",
            name: "Unité 2: Coutumes et traditions",
            weeks: "7-12",
            duration: "12 hours",
            topics: [
              "Vanuatu wedding customs (Tanna)",
              "Local artisanal traditions",
              "World artisanal traditions",
              "Visual and audiovisual document analysis",
              "Creating cultural presentations"
            ],
            objectives: [
              "Analyze visual and audiovisual documents",
              "Identify key questions for descriptions",
              "Create visual aids for presentations",
              "Discover traditions from different cultures"
            ],
            assessment: "Summative: Oral expression (Week 12)"
          }
        ]
      },
      {
        id: "y8-fr-s3",
        name: "Term 3: La communauté et l'environnement",
        code: "FRE-T3",
        color: "purple",
        units: [
          {
            id: "y8-t3-u1",
            name: "Unité 1: La vie au bord de l'eau",
            weeks: "1-6",
            duration: "12 hours",
            topics: [
              "Family life vocabulary",
              "Activities by the water",
              "Describing daily situations and places",
              "Past and future tense introduction",
              "Welcoming French tourists"
            ],
            objectives: [
              "Develop oral and written expression",
              "Describe images of daily situations",
              "Use past and future tenses",
              "Establish links between languages"
            ],
            assessment: "Summative: Oral expression and written comprehension (End of term)"
          },
          {
            id: "y8-t3-u2",
            name: "Unité 2: Protégeons notre environnement",
            weeks: "7-12",
            duration: "12 hours",
            topics: [
              "Natural disasters in Vanuatu",
              "Environmental protection vocabulary",
              "Weather vocabulary",
              "Future wishes and plans",
              "Becoming eco-citizens",
              "Recycling projects"
            ],
            objectives: [
              "Consolidate oral and reading comprehension",
              "Use past, future, and imperative tenses",
              "Learn environmental vocabulary",
              "Engage in environmental projects"
            ],
            assessment: "Summative: Oral expression and comprehension (End of term)"
          }
        ]
      },
      {
        id: "y8-fr-s4",
        name: "Term 4: Approfondissement et projets",
        code: "FRE-T4",
        color: "orange",
        units: [
          {
            id: "y8-t4-u1",
            name: "Unité 1: Projet communautaire",
            weeks: "1-5",
            duration: "10 hours",
            topics: [
              "Community project planning",
              "Local research in French",
              "Interview techniques",
              "Documentation and presentation",
              "Francophone community connections"
            ],
            objectives: [
              "Plan and execute a community project",
              "Conduct research in French",
              "Present findings to an audience"
            ]
          },
          {
            id: "y8-t4-u2",
            name: "Unité 2: Révision et consolidation",
            weeks: "6-10",
            duration: "10 hours",
            topics: [
              "Complete vocabulary review",
              "Grammar consolidation",
              "Oral proficiency development",
              "Written expression mastery",
              "Exam preparation strategies"
            ],
            objectives: [
              "Master all Year 8 content",
              "Demonstrate language proficiency",
              "Prepare for Year 9 French"
            ]
          }
        ]
      }
    ],
    assessmentWeighting: {
      oralComprehension: 25,
      oralExpression: 25,
      readingComprehension: 25,
      writtenExpression: 25
    },
    assessmentTypes: {
      formative: ["Observation grids", "Student work review", "Self-assessment", "Peer assessment"],
      summative: ["Oral presentations", "Written exercises", "Questionnaires", "Creative projects"]
    }
  },
  
  year9: {
    title: "Year 9 French - FLE",
    description: "Students explore themes of school life, travel, festivals, fashion, and history while developing more sophisticated language skills and cultural competence.",
    strands: [
      {
        id: "y9-fr-s1",
        name: "Term 1: La rentrée et les voyages",
        code: "FRE-T1",
        color: "blue",
        units: [
          {
            id: "y9-t1-u1",
            name: "Unité 1: C'est la rentrée!",
            weeks: "1-6",
            duration: "12 hours",
            topics: [
              "Back to school themes",
              "School supplies shopping",
              "Eco-responsible back-to-school",
              "School uniforms discussion",
              "Routines during school period",
              "Francophone and personal presentation review"
            ],
            objectives: [
              "Review and expand Year 7-8 knowledge",
              "Participate in school-related interactions",
              "Express continuously on topics of interest",
              "Interpret texts based on structure and context"
            ]
          },
          {
            id: "y9-t1-u2",
            name: "Unité 2: Nous partons en voyage!",
            weeks: "7-12",
            duration: "12 hours",
            topics: [
              "Travel destinations and weather",
              "Modes of transport",
              "City vocabulary and itineraries",
              "Accommodation descriptions",
              "Expressing preferences",
              "Postcard exchanges"
            ],
            objectives: [
              "Develop oral comprehension and expression",
              "Describe places and give directions",
              "Express preferences about travel",
              "Write postcards and travel descriptions"
            ]
          }
        ]
      },
      {
        id: "y9-fr-s2",
        name: "Term 2: Fêtes et styles",
        code: "FRE-T2",
        color: "green",
        units: [
          {
            id: "y9-t2-u1",
            name: "Unité 1: Comme un air de fête!",
            weeks: "1-6",
            duration: "12 hours",
            topics: [
              "Customary festivals (La circoncision à Tanna)",
              "International festivals (Fête de la musique)",
              "National festivals (Independence Day)",
              "Music and celebrations vocabulary",
              "Past tense review and deepening",
              "Making invitations and appointments"
            ],
            objectives: [
              "Develop cultural competence",
              "Use past tense correctly",
              "Define notions and express opinions",
              "Write invitations and respond to them"
            ]
          },
          {
            id: "y9-t2-u2",
            name: "Unité 2: Question de styles!",
            weeks: "7-12",
            duration: "12 hours",
            topics: [
              "Clothing vocabulary in Vanuatu",
              "Traditional clothing worldwide",
              "Fashion and trends",
              "Using adjectives for descriptions",
              "Word origins and etymology",
              "Expressing tastes and preferences"
            ],
            objectives: [
              "Acquire clothing vocabulary",
              "Use adjectives to enrich descriptions",
              "Explore cultural differences in dress",
              "Express opinions on fashion"
            ]
          }
        ]
      },
      {
        id: "y9-fr-s3",
        name: "Term 3: Histoire et culture",
        code: "FRE-T3",
        color: "purple",
        units: [
          {
            id: "y9-t3-u1",
            name: "Unité 1: Ils ont écrit l'histoire",
            weeks: "1-6",
            duration: "12 hours",
            topics: [
              "Fictional heroes and heroines",
              "Real heroes and heroines",
              "Famous Vanuatu personalities",
              "Biographical writing",
              "Simple future tense",
              "Everyday heroism and civic values"
            ],
            objectives: [
              "Discuss famous historical figures",
              "Compare visual and audiovisual resources",
              "Practice biographical writing",
              "Use future tense for projects and plans"
            ]
          },
          {
            id: "y9-t3-u2",
            name: "Unité 2: Mode et traditions mondiales",
            weeks: "7-10",
            duration: "8 hours",
            topics: [
              "Traditional clothing around the world",
              "Fashion week concepts",
              "Global fashion trends",
              "Physical descriptions vocabulary",
              "Reformulating information",
              "Creating visual presentations"
            ],
            objectives: [
              "Explore global fashion and traditions",
              "Develop description skills",
              "Use visual tools for expression",
              "Compare cultures through clothing"
            ]
          }
        ]
      },
      {
        id: "y9-fr-s4",
        name: "Term 4: Consolidation et projets avancés",
        code: "FRE-T4",
        color: "orange",
        units: [
          {
            id: "y9-t4-u1",
            name: "Unité 1: Projet média",
            weeks: "1-5",
            duration: "10 hours",
            topics: [
              "Media project development",
              "Multimedia creation skills",
              "Creative French expression",
              "Digital storytelling",
              "Presentation techniques"
            ],
            objectives: [
              "Create a multimedia project in French",
              "Use technology for language learning",
              "Present creative work professionally"
            ]
          },
          {
            id: "y9-t4-u2",
            name: "Unité 2: Révision avancée",
            weeks: "6-10",
            duration: "10 hours",
            topics: [
              "Advanced vocabulary mastery",
              "Complex grammar structures",
              "Oral fluency development",
              "Written proficiency",
              "Year 10 preparation"
            ],
            objectives: [
              "Consolidate all Year 9 learning",
              "Achieve intermediate proficiency",
              "Prepare for advanced Year 10 content"
            ]
          }
        ]
      }
    ],
    assessmentWeighting: {
      oralComprehension: 25,
      oralExpression: 25,
      readingComprehension: 25,
      writtenExpression: 25
    },
    assessmentTypes: {
      formative: ["Learning journals", "Worksheet completion", "Peer correction", "Oral participation"],
      summative: ["Oral and written comprehension tests", "Creative projects", "Presentations", "Evaluation grids"]
    }
  },
  
  year10: {
    title: "Year 10 French - FLE",
    description: "Advanced French studies exploring identity, travel, literature, theater, mystery genres, and global citizenship while preparing students for senior secondary studies.",
    strands: [
      {
        id: "y10-fr-s1",
        name: "Term 1: Identité et voyages",
        code: "FRE-T1",
        color: "blue",
        units: [
          {
            id: "y10-t1-u1",
            name: "Unité 1: Moi, mes origines, mon quotidien",
            weeks: "1-5",
            duration: "10 hours",
            topics: [
              "Advanced self-presentation vocabulary",
              "Forming complex questions",
              "Analyzing oral and written presentations",
              "Immediate environment vocabulary",
              "Expression of possession",
              "Creating multimedia presentations"
            ],
            objectives: [
              "Review and deepen presentation skills",
              "Analyze and compare presentation styles",
              "Produce creative personal presentations",
              "Use varied vocabulary and structures"
            ],
            assessment: "Summative: Analysis of audiovisual document (Week 5)"
          },
          {
            id: "y10-t1-u2",
            name: "Unité 2: Voyager en français",
            weeks: "6-11",
            duration: "12 hours",
            topics: [
              "French-speaking world diversity",
              "Tourist brochure analysis",
              "Presenting Francophone countries",
              "Promoting local tourism",
              "Podcast genre discovery",
              "Creating cultural podcasts"
            ],
            objectives: [
              "Discover Francophone cultural diversity",
              "Analyze and create tourist materials",
              "Present countries in French",
              "Create and present podcasts"
            ],
            assessment: "Summative: Podcast presentation (Week 11)"
          }
        ]
      },
      {
        id: "y10-fr-s2",
        name: "Term 2: Littérature et théâtre",
        code: "FRE-T2",
        color: "green",
        units: [
          {
            id: "y10-t2-u1",
            name: "Unité 1: Amours impossibles",
            weeks: "1-6",
            duration: "12 hours",
            topics: [
              "Forbidden love theme vocabulary",
              "Feelings vocabulary",
              "French poetry and songs",
              "Video clip analysis and presentation",
              "Letter and diary formats",
              "Comparing artistic expressions"
            ],
            objectives: [
              "Define and discuss complex themes",
              "Analyze poetry and songs",
              "Read adapted literary texts",
              "Write in letter and diary formats"
            ],
            assessment: "Summative: Diary extract and expressive reading (Week 6)"
          },
          {
            id: "y10-t2-u2",
            name: "Unité 2: Jouer avec les mots",
            weeks: "7-11",
            duration: "10 hours",
            topics: [
              "Molière: Le Bourgeois gentilhomme",
              "French sounds and spellings",
              "Prose vs. verse distinction",
              "Intonation and fluency",
              "Theater codes and vocabulary",
              "Creating original theater scenes"
            ],
            objectives: [
              "Discover French literary heritage",
              "Distinguish prose from verse",
              "Develop dramatic expression",
              "Create and perform theater scenes"
            ]
          }
        ]
      },
      {
        id: "y10-fr-s3",
        name: "Term 3: Mystères et citoyenneté",
        code: "FRE-T3",
        color: "purple",
        units: [
          {
            id: "y10-t3-u1",
            name: "Unité 1: Mystères et univers policier",
            weeks: "1-5",
            duration: "10 hours",
            topics: [
              "Police and detective vocabulary",
              "Audiovisual crime genre analysis",
              "Visual and narrative codes",
              "Detective novel excerpts",
              "Press articles on current events",
              "Character descriptions"
            ],
            objectives: [
              "Discover the detective genre",
              "Identify genre codes and conventions",
              "Read and analyze detective texts",
              "Create descriptions using genre codes"
            ]
          },
          {
            id: "y10-t3-u2",
            name: "Unité 2: Moi, citoyen du monde",
            weeks: "6-10",
            duration: "10 hours",
            topics: [
              "Vanuatu's place in the world",
              "Young citizens in an interconnected world",
              "Economic sectors vocabulary",
              "Environmental issues",
              "Nature and local products vocabulary",
              "Expressing consequences and solutions"
            ],
            objectives: [
              "Explore global citizenship",
              "Discuss environmental and economic issues",
              "Create oral presentations on themes",
              "Compare Vanuatu with Francophone countries"
            ]
          }
        ]
      },
      {
        id: "y10-fr-s4",
        name: "Term 4: Maîtrise et préparation Senior",
        code: "FRE-T4",
        color: "orange",
        units: [
          {
            id: "y10-t4-u1",
            name: "Unité 1: Projet final Junior",
            weeks: "1-5",
            duration: "10 hours",
            topics: [
              "Comprehensive final project",
              "Advanced research techniques",
              "Professional presentation skills",
              "Critical analysis in French",
              "Portfolio development"
            ],
            objectives: [
              "Complete a comprehensive final project",
              "Demonstrate mastery of junior French",
              "Present professionally in French"
            ]
          },
          {
            id: "y10-t4-u2",
            name: "Unité 2: Préparation Senior French",
            weeks: "6-10",
            duration: "10 hours",
            topics: [
              "Complete junior curriculum review",
              "Advanced grammar consolidation",
              "Oral proficiency assessment",
              "Written proficiency assessment",
              "Senior French transition preparation"
            ],
            objectives: [
              "Master all junior French content",
              "Achieve B1 proficiency level",
              "Prepare for Senior French (Years 11-13)"
            ]
          }
        ]
      }
    ],
    assessmentWeighting: {
      oralComprehension: 25,
      oralExpression: 25,
      readingComprehension: 25,
      writtenExpression: 25
    },
    assessmentTypes: {
      formative: ["Preparation sheets", "Written production review", "Performance evaluation", "Self-assessment"],
      summative: ["Audiovisual analysis", "Podcast presentations", "Expressive reading", "Theater performances", "Oral presentations"]
    }
  }
};

// Export for use in other components
export default frenchCurriculum;
